// dataSlice.js
import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import axios from 'axios';
import baseUrl from './baseUrl';

// Define an initial state
const initialState = {
    advertiserSupportRecords: [],
    viewerSupportRecords: [],
    search: [],
    totalAdvertiserRows: 0,
    totalViewerRows: 0,
    status: 'idle',
    error: null,
    data: [],
    comments: []
};

// Define the asynchronous action for fetching advertiserSupportRecords
export const fetchAdvertiserSupportRecords = createAsyncThunk(
    'data/fetchAdvertiserSupportRecords',
    async currentPage => {
        const response = await axios.get(
            `${baseUrl}/support/advertiserList/${currentPage}`
        );
        return response.data;
    }
);

export const fetchViewerSupportRecords = createAsyncThunk(
    'data/fetchViewerSupportRecords',
    async currentPage => {
        const response = await axios.get(
            `${baseUrl}/support/viewerList/${currentPage}`
        );
        return response.data;
    }
);

export const fetchTotalAdvertiserRows = createAsyncThunk(
    'data/fetchTotalAdvertiserRows',
    async userId => {
        const response = await axios.get(
            `${baseUrl}/support/countForAdvertisersList`
        );
        if (response.data > 0) {
            return response.data;
        }
    }
);
export const fetchTotalViewerRows = createAsyncThunk(
    'data/fetchTotalViewerRows',
    async userId => {
        const response = await axios.get(
            `${baseUrl}/support/countForViewersList`
        );
        if (response.data > 0) {
            return response.data;
        }
    }
);
export const fetchSupportDetailsBySupportId = createAsyncThunk(
    'data/fetchSupportDetailsBySupportId',
    async supportId => {
        const response = await axios.get(
            `${baseUrl}/support/ticket/${supportId}`
        );
        return response.data;
    }
);

export const fetchCommentsBySupportID = createAsyncThunk(
    'data/fetchCommentsBySupportID',
    async supportId => {
        try {
            const response = await fetch(
                `${baseUrl}/comments/getData/${supportId}`
            );

            if (!response.ok) {
                throw new Error(`HTTP error! Status: ${response.status}`);
            }

            const responseData = await response.json();

            return responseData;
        } catch (error) {
            console.error('Error fetching data:', error);

            throw error;
        }
    }
);
export const searchAdvertiserSupportList = createAsyncThunk(
    'users/searchAdvertiserSupportList',
    async searchText => {
        const response = await axios.get(
            `${baseUrl}/support/searchAll?role=Advertiser&searchTerm=${searchText.trim()}`
        );
        return response.data;
    }
);
export const searchViewerSupportList = createAsyncThunk(
    'users/searchViewerSupportList',
    async searchText => {
        const response = await axios.get(
            `${baseUrl}/support/searchAll?role=Viewer&searchTerm=${searchText.trim()}`
        );
        return response.data;
    }
);

// Create a slice
const supportSlice = createSlice({
    name: 'support',
    initialState,
    reducers: {},
    extraReducers: builder => {
        builder
            .addCase(fetchAdvertiserSupportRecords.pending, state => {
                state.status = 'loading';
            })
            .addCase(
                fetchAdvertiserSupportRecords.fulfilled,
                (state, action) => {
                    state.status = 'succeeded';
                    state.advertiserSupportRecords = action.payload;
                }
            )
            .addCase(
                fetchAdvertiserSupportRecords.rejected,
                (state, action) => {
                    state.status = 'failed';
                    state.error = action.error.message;
                }
            );
        builder
            .addCase(fetchViewerSupportRecords.pending, state => {
                state.status = 'loading';
            })
            .addCase(fetchViewerSupportRecords.fulfilled, (state, action) => {
                state.status = 'succeeded';
                state.viewerSupportRecords = action.payload;
            })
            .addCase(fetchViewerSupportRecords.rejected, (state, action) => {
                state.status = 'failed';
                state.error = action.error.message;
            });

        builder
            .addCase(fetchTotalAdvertiserRows.pending, state => {
                state.status = 'loading';
            })
            .addCase(fetchTotalAdvertiserRows.fulfilled, (state, action) => {
                state.status = 'succeeded';
                state.totalAdvertiserRows = action.payload;
            })
            .addCase(fetchTotalAdvertiserRows.rejected, (state, action) => {
                state.status = 'failed';
                state.error = action.error.message;
            });
        builder
            .addCase(fetchTotalViewerRows.pending, state => {
                state.status = 'loading';
            })
            .addCase(fetchTotalViewerRows.fulfilled, (state, action) => {
                state.status = 'succeeded';
                state.totalViewerRows = action.payload;
            })
            .addCase(fetchTotalViewerRows.rejected, (state, action) => {
                state.status = 'failed';
                state.error = action.error.message;
            });
        builder
            .addCase(fetchSupportDetailsBySupportId.pending, state => {
                state.status = 'loading';
            })
            .addCase(
                fetchSupportDetailsBySupportId.fulfilled,
                (state, action) => {
                    state.status = 'succeeded';
                    state.data = action.payload;
                }
            )
            .addCase(
                fetchSupportDetailsBySupportId.rejected,
                (state, action) => {
                    state.status = 'failed';
                    state.error = action.error.message;
                }
            );
        builder
            .addCase(fetchCommentsBySupportID.pending, state => {
                state.status = 'loading';
            })
            .addCase(fetchCommentsBySupportID.fulfilled, (state, action) => {
                state.status = 'succeeded';
                state.comments = action.payload;
            })
            .addCase(fetchCommentsBySupportID.rejected, (state, action) => {
                state.status = 'failed';
                state.error = action.error.message;
            });
        builder
            .addCase(searchAdvertiserSupportList.pending, state => {
                state.status = 'loading';
            })
            .addCase(searchAdvertiserSupportList.fulfilled, (state, action) => {
                state.status = 'succeeded';
                state.search = action.payload;
            })
            .addCase(searchAdvertiserSupportList.rejected, (state, action) => {
                state.status = 'failed';
                state.error = action.error.message;
            });
        builder
            .addCase(searchViewerSupportList.pending, state => {
                state.status = 'loading';
            })
            .addCase(searchViewerSupportList.fulfilled, (state, action) => {
                state.status = 'succeeded';
                state.search = action.payload;
            })
            .addCase(searchViewerSupportList.rejected, (state, action) => {
                state.status = 'failed';
                state.error = action.error.message;
            });
    }
});

export default supportSlice.reducer;
